import { useEffect, useRef, useState } from 'react';
import { ChevronsUpDown, LogOut, Plus, Check } from 'lucide-react';
import { Avatar, cn } from '@techsofit/ui';
import { fetchAccountMe, switchProfile, type ProfileView } from '../lib/accountApi';

/**
 * Sidebar account chip + gym switcher. An email can own/work at several gyms; this lists
 * them and switches the active profile, then loads that gym's app. With a single gym it's
 * just the account chip (email + Sign out).
 */
export function AccountMenu({
  displayName,
  gymName,
  email,
  onLogout,
}: {
  displayName: string;
  gymName: string;
  email: string;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [gyms, setGyms] = useState<ProfileView[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // The gyms this account can manage (owner/staff), to offer as switch targets.
  useEffect(() => {
    fetchAccountMe()
      .then((m) => {
        setGyms(m.profiles.filter((p) => p.kind === 'STAFF'));
        setActiveId(m.activeProfileId);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  async function switchTo(p: ProfileView) {
    if (p.id === activeId || switching) return;
    setSwitching(true);
    try {
      await switchProfile(p.id);
    } catch {
      // non-fatal — the destination re-bootstraps from the account session anyway
    }
    const base = import.meta.env.VITE_TENANT_BASE_DOMAIN as string | undefined;
    if (p.slug && base) {
      const port = window.location.port ? `:${window.location.port}` : '';
      window.location.assign(`${window.location.protocol}//${p.slug}.${base}${port}/dashboard`);
    } else {
      window.location.assign('/dashboard');
    }
  }

  const multi = gyms.length > 1;

  return (
    <div ref={ref} className="relative">
      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-2 rounded-control border border-dark-line bg-dark p-1.5 shadow-pop">
          <div className="px-2.5 pb-1 pt-1.5">
            <p className="truncate text-sm font-medium text-on-dark">{displayName}</p>
            {email && <p className="truncate text-xs text-on-dark-muted">{email}</p>}
          </div>

          {multi && (
            <>
              <div className="my-1 h-px bg-dark-line" />
              <p className="px-2.5 py-1 text-[0.7rem] font-semibold uppercase tracking-wide text-on-dark-muted">Your businesses</p>
              {gyms.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  disabled={switching}
                  onClick={() => switchTo(p)}
                  className="flex w-full items-center gap-2.5 rounded-control px-2.5 py-2 text-left text-sm text-on-dark transition-colors hover:bg-dark-soft disabled:opacity-50"
                >
                  <Avatar name={p.orgName ?? 'Business'} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{p.orgName ?? 'Business'}</span>
                    <span className="block truncate text-xs text-on-dark-muted">{p.role === 'OWNER' ? 'Owner' : 'Staff'}{p.coach ? ' · Coach' : ''}</span>
                  </span>
                  {p.id === activeId && <Check className="size-4 shrink-0 text-accent" aria-hidden />}
                </button>
              ))}
              <a
                href="/account?create"
                className="flex w-full items-center gap-2.5 rounded-control px-2.5 py-2 text-left text-sm text-on-dark-muted transition-colors hover:bg-dark-soft hover:text-on-dark"
              >
                <span className="grid size-7 shrink-0 place-items-center rounded-full bg-dark-line">
                  <Plus className="size-4" aria-hidden />
                </span>
                Add a gym
              </a>
            </>
          )}

          <div className="my-1 h-px bg-dark-line" />
          <button
            type="button"
            onClick={onLogout}
            className="flex w-full items-center gap-2.5 rounded-control px-2.5 py-2 text-left text-sm text-on-dark-muted transition-colors hover:bg-dark-soft hover:text-on-dark"
          >
            <span className="grid size-7 shrink-0 place-items-center rounded-full bg-dark-line">
              <LogOut className="size-4" aria-hidden />
            </span>
            Sign out
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex w-full items-center gap-3 rounded-control bg-dark-soft p-2.5 text-left transition-colors hover:bg-dark-line"
      >
        <Avatar name={gymName || displayName} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-on-dark">{gymName || displayName}</p>
          <p className={cn('truncate text-xs text-on-dark-muted')}>{multi ? 'Switch business' : displayName}</p>
        </div>
        <ChevronsUpDown className="size-4 shrink-0 text-on-dark-muted" aria-hidden />
      </button>
    </div>
  );
}
